import { api } from './apiClient.js';

/**
 * List workspaces with pagination, sort, and optional keyword search.
 *
 * Typical body (snake_case):
 * `{ page, page_size, order_by, order_direction, search?: { keyword } }`
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const listWorkspaces = (body) => api.post('/v0/workspace/list', body);

/**
 * Workspace detail by UUID (tenant).
 *
 * @param {string} workspaceId
 * @returns {Promise<unknown>}
 */
export const getWorkspaceById = (workspaceId) =>
  api.get(`/v0/workspace/${encodeURIComponent(workspaceId)}`);

/**
 * Create workspace (POST /v1/workspace/create).
 *
 * Expected payload:
 *   name            – workspace display name (user input)
 *   slug            – derived from name (auto-generated)
 *   contact_email   – owner email (user input)
 *   contact_name    – contact name (user input)
 *   contact_address – address (optional user input)
 *   phone_number    – phone (hardcoded for now)
 *   billing_plan    – billing plan (hardcoded for now)
 *   organization_id – organization id (hardcoded for now)
 *
 * TODO: phone_number, billing_plan, and organization_id are hardcoded in the UI.
 * They should come from the signed-in user session or dedicated fields when available.
 *
 * Logo: first `getUploadUrl` + PUT to S3; `path` = `s3_path` from upload-url;
 * `filename`, `logical_name` (stem without extension), and `file_type` from the uploaded file.
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const createWorkspace = (body) => api.post('/v0/workspace/create', body);

/**
 * Update workspace — same field set as create where applicable, plus `workspace_uuid`.
 * Typical shape: `workspace_uuid`, `name`, `slug`, `contact_email`, `contact_name`, `phone_number`,
 * `billing_plan`, `organization_id`, optional `contact_address`, and with a new logo after S3:
 * `logical_name`, `filename`, `file_type`, `path`.
 * Owner-only updates may still use `workspace_uuid` + `owner_email` (see ChangeWorkspaceOwnerModal).
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const updateWorkspace = (body) => api.post('/v0/workspace/update', body);

/**
 * Tell the backend about a profile photo already uploaded to storage (POST /v1/workspace/photo).
 *
 * Typical body:
 * `{ logical_name, filename, path, file_type, md5 }` — `filename` and `file_type` from the uploaded file;
 * `logical_name` = file name **without** extension (e.g. `logo.png` → `logo`);
 * `path` = `s3_path` from POST /v1/file/upload-url; `md5` = lowercase hex digest of the binary.
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const uploadWorkspacePhoto = (body) => api.post('/v0/workspace/photo', body);

/**
 * Reactivate workspace.
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const activateWorkspace = (body) => api.post('/v0/workspace/activate', body);

/**
 * Deactivate workspace.
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const deactivateWorkspace = (body) => api.post('/v0/workspace/deactivate', body);
